import type {
  CreateShiftTemplateDto,
  UpdateShiftTemplateDto,
} from '@/types/shift.types';
import { completeClock, toClock } from './shiftDraftModel';

export interface ShiftTemplateDraftRow {
  key: string;
  employeeUuid: string | null;
  position: string | null;
  plannedStart: string;
  plannedEnd: string;
  paymentSchemeTemplateUuid: string | null;
}

export interface ShiftTemplateFieldErrors {
  employee?: string;
  plannedStart?: string;
  plannedEnd?: string;
}

export interface ShiftTemplateErrors {
  form?: string;
  responsible?: string;
  rows: Record<string, ShiftTemplateFieldErrors>;
}

const clockToMinutes = (value: string): number | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(completeClock(value));
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};

export const validateTemplateName = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return 'Введите имя';
  if (trimmed.length > 255) return 'Имя не длиннее 255 символов';
  return null;
};

export const validateShiftTemplate = (draft: {
  responsibleEmployeeUuid: string | null;
  rows: ShiftTemplateDraftRow[];
}): ShiftTemplateErrors => {
  const errors: ShiftTemplateErrors = { rows: {} };
  const filled = draft.rows.filter((row) => Boolean(row.employeeUuid));

  if (filled.length === 0) {
    errors.form = 'Выберите сотрудника';
  }

  const seen = new Set<string>();
  for (const row of filled) {
    const employeeUuid = row.employeeUuid as string;
    const rowErrors: ShiftTemplateFieldErrors = {};

    if (seen.has(employeeUuid)) {
      rowErrors.employee = 'Сотрудник указан в шаблоне дважды';
    } else {
      seen.add(employeeUuid);
    }

    const start = row.plannedStart ? clockToMinutes(row.plannedStart) : null;
    const end = row.plannedEnd ? clockToMinutes(row.plannedEnd) : null;
    if (!row.plannedStart || start === null) rowErrors.plannedStart = 'Укажите время';
    if (!row.plannedEnd || end === null) rowErrors.plannedEnd = 'Укажите время';
    if (start !== null && end !== null && start >= end) {
      rowErrors.plannedEnd = 'Начало должно быть раньше конца';
    }

    if (Object.keys(rowErrors).length > 0) errors.rows[row.key] = rowErrors;
  }

  if (!draft.responsibleEmployeeUuid || !seen.has(draft.responsibleEmployeeUuid)) {
    errors.responsible = 'Ответственный должен входить в строки шаблона';
  }

  return errors;
};

export const templateClock = (value: string): string => {
  if (!value.trim()) return '';
  const truncated = toClock(value);
  return completeClock(truncated || value);
};

export const toCreateShiftTemplatePayload = (input: {
  name: string;
  serviceCenterUuid: string;
  responsibleEmployeeUuid: string;
  rows: ShiftTemplateDraftRow[];
}): CreateShiftTemplateDto => ({
  service_center_uuid: input.serviceCenterUuid,
  name: input.name.trim(),
  responsible_employee_uuid: input.responsibleEmployeeUuid,
  rows: input.rows
    .filter((row): row is ShiftTemplateDraftRow & { employeeUuid: string } =>
      Boolean(row.employeeUuid),
    )
    .map((row) => ({
      employee_uuid: row.employeeUuid,
      planned_start: templateClock(row.plannedStart),
      planned_end: templateClock(row.plannedEnd),
      payment_scheme_template_uuid: row.paymentSchemeTemplateUuid,
    })),
});

export const toUpdateShiftTemplatePayload = (input: {
  name: string;
  responsibleEmployeeUuid: string;
  rows: ShiftTemplateDraftRow[];
}): UpdateShiftTemplateDto => ({
  name: input.name.trim(),
  responsible_employee_uuid: input.responsibleEmployeeUuid,
  rows: input.rows
    .filter((row): row is ShiftTemplateDraftRow & { employeeUuid: string } =>
      Boolean(row.employeeUuid),
    )
    .map((row) => ({
      employee_uuid: row.employeeUuid,
      planned_start: templateClock(row.plannedStart),
      planned_end: templateClock(row.plannedEnd),
      payment_scheme_template_uuid: row.paymentSchemeTemplateUuid,
    })),
});

export const hasShiftTemplateErrors = (errors: ShiftTemplateErrors): boolean =>
  Boolean(
    errors.form ||
      errors.responsible ||
      Object.values(errors.rows).some((row) => Object.keys(row).length > 0),
  );

const rowKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `row-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const emptyTemplateDraftRow = (): ShiftTemplateDraftRow => ({
  key: rowKey(),
  employeeUuid: null,
  position: null,
  plannedStart: '',
  plannedEnd: '',
  paymentSchemeTemplateUuid: null,
});
